import React, { useState, useMemo, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Rectangle, useMapEvents } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { LatLngExpression, LeafletMouseEvent, Map as LeafletMap } from 'leaflet';
import L from 'leaflet';
import { v4 as uuidv4 } from 'uuid';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import {
  setCurrentView,
  navigateNext,
  navigatePrevious,
  navigateToToday,
  addEvent,
  clearAllEvents
} from '../../store/slices/calendarSlice';
import { clearCalendarTasks } from '../../store/slices/todoSlice';
import { CalendarEvent } from '../../types/calendar';
import { OrderStatus, Order, OrderItem } from '../../types/order';
import CalendarHeader from './components/CalendarHeader';
import MonthView from './components/MonthView';
import WeekView from './components/WeekView';
import DayView from './components/DayView';
import EventModal from './components/EventModal';
import ScheduleDeliveriesModal from './components/ScheduleDeliveriesModal';
import DayPlanner from '../todo/components/DayPlanner';
import HomeButtonControl from './components/HomeButtonControl';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { HOME_COORDINATES } from '../../constants/appConfig';
// Geocoding is now handled automatically by Redux middleware
// No manual geocoding imports needed

/**
 * Calculates days since order date
 * @param orderDate - ISO date string
 * @returns number of days
 */
const getDaysSinceOrder = (orderDate: string): number => {
  if (!orderDate) return 0;

  const [year, month, day] = orderDate.split('-').map(Number);
  const orderDateTime = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffTime = today.getTime() - orderDateTime.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
};

/**
 * Gets color based on order age
 * @param days - number of days since order
 * @param status - order status
 * @returns hex color string
 */
const getOrderAgeColor = (days: number, status: OrderStatus): string => {
  // Completed orders get gray
  if (status === 'Completed') {
    return '#9ca3af'; // Gray
  }

  // Age-based colors
  if (days <= 1) return '#10b981'; // Green (Fresh - 0-1 days)
  if (days <= 3) return '#84cc16'; // Yellow-Green (Recent - 2-3 days)
  if (days <= 5) return '#eab308'; // Yellow (Warning - 4-5 days)
  if (days === 6) return '#f97316'; // Orange (Urgent - 6 days)
  return '#ef4444'; // Red (Overdue - 7+ days)
};

/**
 * Determines if a DOM element or its ancestors is a Leaflet marker
 * @param target - The DOM element that was clicked
 * @returns true if the element is part of a marker, false otherwise
 */
const isMarkerElement = (target: HTMLElement | null): boolean => {
  if (!target) return false;

  // Check for marker-related classes
  const markerClasses = ['leaflet-marker-icon', 'custom-delivery-icon', 'custom-cluster-icon', 'custom-home-icon'];

  // Walk up the DOM tree to check if any ancestor is a marker
  let element: HTMLElement | null = target;
  while (element && element !== document.body) {
    if (markerClasses.some(cls => element!.classList.contains(cls))) {
      return true;
    }
    element = element.parentElement;
  }

  return false;
};

/**
 * Component to handle map events for rectangle selection and background clicks
 */
interface MapEventHandlerProps {
  onStartSelection: (latlng: L.LatLng) => void;
  onUpdateSelection: (latlng: L.LatLng) => void;
  onEndSelection: () => void;
  onMapBackgroundClick: () => void;
  isSelecting: boolean;
  mapRef: React.MutableRefObject<LeafletMap | null>;
}

const MapEventHandler: React.FC<MapEventHandlerProps> = ({
  onStartSelection,
  onUpdateSelection,
  onEndSelection,
  onMapBackgroundClick,
  isSelecting,
  mapRef
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const map = useMapEvents({
    mousedown: (e: LeafletMouseEvent) => {
      const originalEvent = e.originalEvent as MouseEvent;
      if (originalEvent.shiftKey) {
        // Check if clicking on a marker (including nested elements)
        const target = originalEvent.target as HTMLElement;

        if (!isMarkerElement(target)) {
          // Only start rectangle selection on map background
          onStartSelection(e.latlng);
          map.dragging.disable();
          e.originalEvent.preventDefault();
        }
        // If clicking on marker, do nothing - let marker handler take over
      } else {
        // Track potential drag start
        setIsDragging(false);
      }
    },
    mousemove: (e: LeafletMouseEvent) => {
      if (isSelecting) {
        onUpdateSelection(e.latlng);
        e.originalEvent.preventDefault();
      } else if (map.dragging.enabled()) {
        // If map is being dragged, mark as dragging
        setIsDragging(true);
      }
    },
    mouseup: () => {
      if (isSelecting) {
        onEndSelection();
        map.dragging.enable();
      }
    },
    click: () => {
      // Only clear selections if:
      // 1. Not currently in rectangle selection mode
      // 2. User didn't drag the map (was a true click)
      if (!isSelecting && !isDragging) {
        onMapBackgroundClick();
      }
      // Reset drag state
      setIsDragging(false);
    }
  });

  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);

  return null;
};

const Calendar: React.FC = () => {
  const dispatch = useAppDispatch();
  const { events, currentView, currentDate } = useAppSelector(state => state.calendar);
  const { orders } = useAppSelector(state => state.orders);
  const { tasks } = useAppSelector(state => state.todo);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isMapView, setIsMapView] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<string>>(new Set());
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);

  // Rectangle selection state
  const [isRectSelecting, setIsRectSelecting] = useState(false);
  const [rectStart, setRectStart] = useState<L.LatLng | null>(null);
  const [rectEnd, setRectEnd] = useState<L.LatLng | null>(null);

  const orderCardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const mapRef = useRef<LeafletMap | null>(null);

  // Map center coordinates - using centralized home coordinates
  const mapCenter: LatLngExpression = [HOME_COORDINATES.lat, HOME_COORDINATES.lng];

  // Create custom home icon
  const homeIcon = L.divIcon({
    className: 'custom-home-icon',
    html: `
      <div style="
        width: 40px;
        height: 40px;
        background-color: #ef4444;
        border: 3px solid white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      ">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          <polyline points="9 22 9 12 15 12 15 22"></polyline>
        </svg>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 20]
  });

  // Create custom delivery marker icon
  const createDeliveryIcon = (order: Order, isSelected: boolean = false) => {
    const daysSince = getDaysSinceOrder(order.orderDate);
    const ageColor = getOrderAgeColor(daysSince, order.status);

    // Apply highlight styles matching delivery card highlight
    const highlightStyles = isSelected
      ? `box-shadow: 0 0 0 4px rgba(33, 150, 243, 0.4), 0 4px 12px rgba(33, 150, 243, 0.3);
         outline: 2px solid #2196F3;
         outline-offset: 2px;`
      : 'box-shadow: 0 2px 6px rgba(0,0,0,0.3);';

    return L.divIcon({
      className: `custom-delivery-icon${isSelected ? ' selected' : ''}`,
      html: `
        <div style="
          width: 32px;
          height: 32px;
          background-color: ${ageColor};
          border: 2px solid white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          ${highlightStyles}
          cursor: pointer;
        ">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
        </div>
      `,
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    });
  };

  // Create custom cluster icon
  const createClusterIcon = (cluster: any) => {
    const count = cluster.getChildCount();

    // Check if any markers in the cluster are selected
    const childMarkers = cluster.getAllChildMarkers();
    const hasSelectedMarker = childMarkers.some((marker: any) => {
      // Each marker has options.orderId that we'll set
      const orderId = marker.options?.orderId;
      return orderId && selectedOrderIds.includes(orderId);
    });

    // Determine cluster size and color based on count
    let size = 40;
    let color = '#2196F3';

    if (count >= 10) {
      size = 50;
      color = '#f97316'; // Orange for large clusters
    } else if (count >= 5) {
      size = 45;
      color = '#eab308'; // Yellow for medium clusters
    }

    // Apply highlight styles if cluster contains selected markers
    const highlightStyles = hasSelectedMarker
      ? `box-shadow: 0 0 0 4px rgba(33, 150, 243, 0.4), 0 4px 12px rgba(33, 150, 243, 0.3);
         outline: 2px solid #2196F3;
         outline-offset: 2px;`
      : 'box-shadow: 0 2px 8px rgba(0,0,0,0.4);';

    return L.divIcon({
      className: `custom-cluster-icon${hasSelectedMarker ? ' selected' : ''}`,
      html: `
        <div style="
          width: ${size}px;
          height: ${size}px;
          background-color: ${color};
          border: 3px solid white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          ${highlightStyles}
          font-weight: bold;
          font-size: ${size >= 50 ? '16px' : '14px'};
          color: white;
        ">
          ${count}
        </div>
      `,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2]
    });
  };

  // Filter orders with "ready" badge for deliveries section
  // Exclude orders that have been scheduled (have a calendar event with their orderId)
  const readyOrders = useMemo(() => {
    // Create a Set of scheduled order IDs for efficient lookup
    const scheduledOrderIds = new Set(
      events
        .filter(event => event.orderId) // Only events with orderId
        .map(event => event.orderId)
    );

    return orders
      .filter((order: Order) => {
        // Must be ready and not completed
        if (!order.isReady || order.status === 'Completed') return false;
        // Must not be scheduled
        return !scheduledOrderIds.has(order.id);
      })
      .sort((a: Order, b: Order) => {
        // Sort by orderDate ascending (oldest first, newest last)
        return a.orderDate.localeCompare(b.orderDate);
      });
  }, [orders, events]);

  // Toggle order card expansion (accordion behavior - only one card expanded at a time)
  const toggleOrderExpansion = (orderId: string) => {
    setExpandedOrderIds(prev => {
      // If this card is already expanded, collapse it
      if (prev.has(orderId)) {
        return new Set();
      }
      // Otherwise, expand only this card (collapse all others)
      return new Set([orderId]);
    });
  };

  // Handle delivery card click to select and pan map
  const handleDeliveryCardClick = (orderId: string, shiftKey: boolean) => {
    // Toggle expansion
    toggleOrderExpansion(orderId);

    // Pan map to marker location
    const order = readyOrders.find((o: Order) => o.id === orderId);
    if (order && mapRef.current && order.manualGeocode) {
      // Smoothly pan to the marker location
      mapRef.current.flyTo([order.manualGeocode.lat, order.manualGeocode.lng], 15, {
        duration: 1.5, // 1.5 seconds animation
        easeLinearity: 0.25
      });
    }
  };

  // Handle checkbox toggle for delivery selection
  const handleCheckboxToggle = (orderId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click from firing
    setSelectedOrderIds(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  // Handle marker click to scroll to order and manage multi-select
  const handleMarkerClick = (orderId: string, shiftKey: boolean) => {
    if (shiftKey) {
      // Shift+click: toggle marker in selection
      setSelectedOrderIds(prev => {
        if (prev.includes(orderId)) {
          // Remove from selection
          return prev.filter(id => id !== orderId);
        } else {
          // Add to selection
          return [...prev, orderId];
        }
      });
    } else {
      // Normal click: single selection (clears others)
      setSelectedOrderIds([orderId]);
    }

    // Auto-scroll to the order card
    setTimeout(() => {
      const orderElement = orderCardRefs.current[orderId];
      if (orderElement) {
        orderElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }, 100);
  };

  // Handle rectangle selection start
  const handleStartSelection = (latlng: L.LatLng) => {
    setIsRectSelecting(true);
    setRectStart(latlng);
    setRectEnd(latlng);
  };

  // Handle rectangle selection update
  const handleUpdateSelection = (latlng: L.LatLng) => {
    if (isRectSelecting) {
      setRectEnd(latlng);
    }
  };

  // Handle rectangle selection end
  const handleEndSelection = () => {
    if (rectStart && rectEnd) {
      // Create bounds from selection rectangle
      const bounds = L.latLngBounds(rectStart, rectEnd);

      // Find all markers within bounds
      const selectedIds: string[] = [];
      readyOrders.forEach((order: Order) => {
        if (order.manualGeocode && bounds.contains([order.manualGeocode.lat, order.manualGeocode.lng])) {
          selectedIds.push(order.id);
        }
      });

      // Add selected markers to existing selection (additive)
      setSelectedOrderIds(prev => {
        const combined = [...prev, ...selectedIds];
        // Remove duplicates
        return Array.from(new Set(combined));
      });
    }

    // Reset selection state
    setIsRectSelecting(false);
    setRectStart(null);
    setRectEnd(null);
  };

  // Handle map background click to clear all selections
  const handleMapBackgroundClick = () => {
    setSelectedOrderIds([]);
  };

  // Geocoding is now handled automatically by the Redux middleware
  // Orders will have manualGeocode set when they're created/updated

  const handleViewChange = (view: 'month' | 'week' | 'day') => {
    dispatch(setCurrentView(view));
  };

  const handlePrevious = () => {
    dispatch(navigatePrevious());
  };

  const handleNext = () => {
    dispatch(navigateNext());
  };

  const handleToday = () => {
    dispatch(navigateToToday());
  };

  const handleCreateEvent = () => {
    setSelectedDate(null);
    setSelectedEvent(null);
    setIsModalOpen(true);
  };

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setSelectedDate(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
    setSelectedDate(null);
  };

  const handleToggleMapView = () => {
    setIsMapView(!isMapView);
  };

  const handleOpenScheduleModal = () => {
    setIsScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setIsScheduleModalOpen(false);
  };

  const handleConfirmSchedule = (deliveryDate: Date) => {
    // Format the delivery date to ISO string (YYYY-MM-DD)
    const dateISO = deliveryDate.toISOString().split('T')[0];
    const now = new Date().toISOString();

    // Create a calendar event for each selected delivery
    selectedOrderIds.forEach(orderId => {
      const order = readyOrders.find((o: Order) => o.id === orderId);
      if (order) {
        const itemCount = order.items.length;
        const event: CalendarEvent = {
          id: uuidv4(),
          title: `Delivery: ${order.customerName} (${itemCount} item${itemCount !== 1 ? 's' : ''})`,
          description: `Delivery for ${order.customerName}\nOrder Date: ${order.orderDate}\nItems: ${order.items.map((item: OrderItem) => `${item.recipeName} x${item.quantity}`).join(', ')}`,
          startDate: dateISO,
          endDate: dateISO,
          startTime: '09:00',
          endTime: '10:00',
          color: '#4CAF50', // Green color for deliveries
          allDay: false,
          createdAt: now,
          updatedAt: now,
          orderId: orderId, // Link to the order
          completed: false // Initially not completed
        };
        dispatch(addEvent(event));
      }
    });

    // Clear selections after scheduling
    setSelectedOrderIds([]);
  };

  const handleClearCalendar = () => {
    const confirmed = window.confirm(
      'Clear all calendar items? This will remove all scheduled items, badges, and markers.'
    );
    if (confirmed) {
      dispatch(clearAllEvents());
      dispatch(clearCalendarTasks());
    }
  };

  // Geocoding overlay removed - middleware handles geocoding automatically

  return (
    <div style={styles.container}>
      <div style={styles.buttonContainer}>
        <button onClick={handleCreateEvent} style={styles.createEventButton}>
          Create Event
        </button>
        <button onClick={handleToggleMapView} style={styles.mapViewButton}>
          {isMapView ? 'Calendar View' : 'Map View'}
        </button>
        <button onClick={handleClearCalendar} style={styles.clearCalendarButton}>
          Clear Calendar
        </button>
      </div>

      {!isMapView ? (
        <div style={styles.calendarViewWrapper}>
          <div style={styles.calendarSection}>
            <CalendarHeader
              currentView={currentView}
              currentDate={currentDate}
              onViewChange={handleViewChange}
              onPrevious={handlePrevious}
              onNext={handleNext}
              onToday={handleToday}
            />

            <div style={styles.content}>
              {currentView === 'month' && (
                <MonthView
                  currentDate={currentDate}
                  events={events}
                  orders={orders}
                  tasks={tasks}
                  onEventClick={handleEventClick}
                />
              )}
              {currentView === 'week' && (
                <WeekView currentDate={currentDate} events={events} orders={orders} onEventClick={handleEventClick} />
              )}
              {currentView === 'day' && (
                <DayView currentDate={currentDate} events={events} orders={orders} onEventClick={handleEventClick} />
              )}
            </div>
          </div>
          <div style={styles.dayPlannerSection}>
            <ErrorBoundary sectionName="Day Planner">
              <DayPlanner />
            </ErrorBoundary>
          </div>
        </div>
      ) : (
        <ErrorBoundary sectionName="Delivery">
          <div
            style={{
              ...styles.mapViewWrapper,
              ...styles.mapViewWrapperConstrained
            }}
          >
            <div style={styles.mapViewSection}>
              <MapContainer
                // @ts-ignore - react-leaflet v5 type definitions issue
                center={mapCenter}
                zoom={10}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  // @ts-ignore - react-leaflet v5 type definitions issue
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <MapEventHandler
                  onStartSelection={handleStartSelection}
                  onUpdateSelection={handleUpdateSelection}
                  onEndSelection={handleEndSelection}
                  onMapBackgroundClick={handleMapBackgroundClick}
                  isSelecting={isRectSelecting}
                  mapRef={mapRef}
                />
                {isRectSelecting && rectStart && rectEnd && (
                  <Rectangle
                    bounds={[
                      [rectStart.lat, rectStart.lng],
                      [rectEnd.lat, rectEnd.lng]
                    ]}
                    pathOptions={{
                      color: '#2196F3',
                      weight: 2,
                      fillColor: '#2196F3',
                      fillOpacity: 0.2
                    }}
                  />
                )}
                <Marker position={mapCenter} {...({ icon: homeIcon } as any)} />
                {/* Render delivery markers with clustering */}
                <MarkerClusterGroup
                  iconCreateFunction={createClusterIcon}
                  showCoverageOnHover={false}
                  spiderfyOnMaxZoom={true}
                  zoomToBoundsOnClick={true}
                  maxClusterRadius={60}
                >
                  {readyOrders.map((order: Order) => {
                    // Only display orders with geocoded coordinates
                    if (!order.manualGeocode) return null;

                    const location = { lat: order.manualGeocode.lat, lng: order.manualGeocode.lng };

                    const itemCount = order.items.length;
                    const isSelected = selectedOrderIds.includes(order.id);

                    return (
                      <Marker
                        key={order.id}
                        position={[location.lat, location.lng] as LatLngExpression}
                        {...({
                          icon: createDeliveryIcon(order, isSelected),
                          orderId: order.id
                        } as any)}
                        eventHandlers={{
                          click: (e: LeafletMouseEvent) => {
                            const shiftKey = (e.originalEvent as MouseEvent).shiftKey;
                            handleMarkerClick(order.id, shiftKey);
                            // Prevent map background click handler from firing
                            e.originalEvent.stopPropagation();
                          }
                        }}
                      >
                        <Popup>
                          <div style={{ fontSize: '14px', fontWeight: '500' }}>
                            {order.customerName} - {itemCount} item{itemCount !== 1 ? 's' : ''}
                          </div>
                          <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                            📍 {order.manualGeocode.setBy === 'auto-geocoding' ? 'Auto-geocoded' : 'Manual location'}
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MarkerClusterGroup>
                <HomeButtonControl center={mapCenter} />
              </MapContainer>
            </div>
            <div style={styles.deliveriesSection}>
              <div style={styles.deliveriesHeader}>
                <h3 style={styles.deliveriesTitle}>Deliveries</h3>
              </div>
              <div style={styles.deliveriesContent}>
                {readyOrders.length > 0 ? (
                  <>
                    <div style={styles.ordersList}>
                      {readyOrders.map((order: Order) => {
                        const daysSince = getDaysSinceOrder(order.orderDate);
                        const ageColor = getOrderAgeColor(daysSince, order.status);
                        const isExpanded = expandedOrderIds.has(order.id);
                        const isSelected = selectedOrderIds.includes(order.id);

                        return (
                          <div
                            key={order.id}
                            ref={el => {
                              orderCardRefs.current[order.id] = el;
                            }}
                            style={{
                              ...styles.orderCard,
                              borderLeft: `4px solid ${ageColor}`,
                              cursor: 'pointer',
                              ...(isSelected && styles.orderCardHighlighted)
                            }}
                            onClick={e => handleDeliveryCardClick(order.id, e.shiftKey)}
                          >
                            {isExpanded ? (
                              <>
                                <div style={styles.orderHeader}>
                                  <div style={styles.orderInfo}>
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={e => handleCheckboxToggle(order.id, e as any)}
                                      onClick={e => e.stopPropagation()}
                                      style={styles.checkbox}
                                    />
                                    <span style={styles.customerName}>{order.customerName}</span>
                                    <span style={styles.readyBadge}>READY</span>
                                    <svg
                                      style={styles.chevronExpanded}
                                      width="16"
                                      height="16"
                                      viewBox="0 0 16 16"
                                      fill="none"
                                    >
                                      <path
                                        d="M4 6L8 10L12 6"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </div>
                                  <div style={styles.orderMeta}>
                                    <span style={styles.orderDate}>{order.orderDate}</span>
                                    <span style={{ ...styles.ageBadge, backgroundColor: ageColor }}>
                                      {daysSince} day{daysSince !== 1 ? 's' : ''} old
                                    </span>
                                  </div>
                                </div>
                                <div style={styles.orderDetails}>
                                  <span style={styles.itemCount}>
                                    {order.items.length} item{order.items.length !== 1 ? 's' : ''}
                                  </span>
                                  <span style={styles.orderStatus}>{order.status}</span>
                                </div>
                                {order.items.length > 0 && (
                                  <div style={styles.itemsList}>
                                    {order.items.map((item, idx) => (
                                      <div key={idx} style={styles.orderItem}>
                                        <span style={styles.itemName}>{item.recipeName}</span>
                                        <span style={styles.itemQuantity}>×{item.quantity}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </>
                            ) : (
                              <div style={styles.collapsedCard}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={e => handleCheckboxToggle(order.id, e as any)}
                                  onClick={e => e.stopPropagation()}
                                  style={styles.checkbox}
                                />
                                <svg
                                  style={styles.chevronCollapsed}
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <path
                                    d="M4 6L8 10L12 6"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                <span style={styles.customerName}>{order.customerName}</span>
                                <span style={styles.collapsedItemCount}>
                                  {order.items.length} item{order.items.length !== 1 ? 's' : ''}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <button
                      disabled={selectedOrderIds.length === 0}
                      onClick={handleOpenScheduleModal}
                      style={{
                        ...styles.scheduleButton,
                        ...(selectedOrderIds.length === 0 && styles.scheduleButtonDisabled)
                      }}
                    >
                      Schedule Selected Deliveries
                    </button>
                  </>
                ) : (
                  <p style={styles.noDeliveriesMessage}>No orders ready for delivery</p>
                )}
              </div>
            </div>
          </div>
        </ErrorBoundary>
      )}

      {isModalOpen && (
        <EventModal event={selectedEvent} initialDate={selectedDate || currentDate} onClose={handleCloseModal} />
      )}

      <ScheduleDeliveriesModal
        isOpen={isScheduleModalOpen}
        onClose={handleCloseScheduleModal}
        onConfirm={handleConfirmSchedule}
        selectedOrderIds={selectedOrderIds}
        orders={readyOrders}
      />
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: '20px',
    backgroundColor: '#f5f5f5'
  },
  buttonContainer: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px'
  },
  createEventButton: {
    padding: '12px 24px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  mapViewButton: {
    padding: '12px 24px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  clearCalendarButton: {
    padding: '12px 24px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  calendarViewWrapper: {
    display: 'flex',
    gap: '20px',
    flex: 1,
    minHeight: '500px'
  },
  calendarSection: {
    flex: '2',
    display: 'flex',
    flexDirection: 'column'
  },
  dayPlannerSection: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  content: {
    flex: 1,
    marginTop: '20px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    overflow: 'auto'
  },
  mapViewWrapper: {
    display: 'flex',
    gap: '20px',
    marginTop: '20px',
    flex: 1,
    minHeight: '500px'
  },
  mapViewSection: {
    flex: '2',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'auto',
    position: 'relative'
  },
  mapViewPlaceholder: {
    fontSize: '24px',
    color: '#757575',
    fontWeight: '500'
  },
  deliveriesSection: {
    flex: '1',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  deliveriesHeader: {
    padding: '20px',
    borderBottom: '2px solid #f0f0f0',
    backgroundColor: '#fafafa'
  },
  deliveriesTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '700',
    color: '#333'
  },
  deliveriesContent: {
    flex: 1,
    padding: '20px',
    overflow: 'auto'
  },
  ordersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  orderCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '12px',
    transition: 'all 0.3s ease-in-out',
    overflow: 'hidden'
  },
  orderCardHighlighted: {
    boxShadow: '0 0 0 4px rgba(33, 150, 243, 0.4), 0 4px 12px rgba(33, 150, 243, 0.3)',
    outline: '2px solid #2196F3',
    outlineOffset: '2px',
    backgroundColor: '#f0f8ff'
  },
  orderHeader: {
    marginBottom: '8px'
  },
  orderInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px'
  },
  customerName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333'
  },
  readyBadge: {
    fontSize: '11px',
    padding: '3px 6px',
    backgroundColor: '#4CAF50',
    color: 'white',
    borderRadius: '3px',
    fontWeight: 'bold'
  },
  orderMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#666'
  },
  orderDate: {
    fontSize: '13px',
    color: '#666'
  },
  ageBadge: {
    fontSize: '11px',
    padding: '3px 6px',
    borderRadius: '3px',
    color: 'white',
    fontWeight: 'bold'
  },
  orderDetails: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '13px',
    color: '#666',
    marginBottom: '8px',
    paddingTop: '8px',
    borderTop: '1px solid #f0f0f0'
  },
  itemCount: {
    fontSize: '13px',
    color: '#666'
  },
  orderStatus: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#2196F3'
  },
  itemsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    paddingTop: '8px',
    borderTop: '1px solid #f0f0f0'
  },
  orderItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '13px',
    padding: '4px 8px',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px'
  },
  itemName: {
    color: '#333',
    fontWeight: '500'
  },
  itemQuantity: {
    color: '#007bff',
    fontWeight: '600'
  },
  noDeliveriesMessage: {
    margin: 0,
    fontSize: '15px',
    color: '#999',
    fontStyle: 'italic',
    textAlign: 'center'
  },
  collapsedCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  chevronExpanded: {
    color: '#666',
    transition: 'transform 0.3s ease-in-out',
    transform: 'rotate(180deg)',
    flexShrink: 0
  },
  chevronCollapsed: {
    color: '#666',
    transition: 'transform 0.3s ease-in-out',
    transform: 'rotate(0deg)',
    flexShrink: 0
  },
  collapsedItemCount: {
    fontSize: '14px',
    color: '#666',
    marginLeft: 'auto'
  },
  buttonContainerSticky: {
    position: 'sticky',
    top: '20px',
    zIndex: 10,
    backgroundColor: '#f5f5f5',
    paddingTop: '8px',
    paddingBottom: '8px'
  },
  mapViewWrapperConstrained: {
    position: 'sticky',
    top: '88px', // Account for button container height + padding
    height: 'calc(100vh - 108px)', // Full viewport minus button container and padding
    maxHeight: 'calc(100vh - 108px)',
    overflow: 'hidden'
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#2196F3',
    flexShrink: 0
  },
  scheduleButton: {
    width: '100%',
    padding: '12px 24px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s, opacity 0.2s',
    marginTop: '16px'
  },
  scheduleButtonDisabled: {
    backgroundColor: '#b0bec5',
    cursor: 'not-allowed',
    opacity: 0.6
  }
};

export default Calendar;
